import React, { useState, useEffect } from 'react';
import { Box, Dialog, DialogTitle, DialogActions, Button, Typography, CircularProgress } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RecipeSelection from '../../../components/plans/RecipeSelection';
import Recommendations from '../../../components/plans/Recommendations';
import api from '../../../services/api';

const CoachEditPlan = () => {
  const { clientId, weekPlanId } = useParams();
  const [recipes, setRecipes] = useState([]);
  const [weekNumber, setWeekNumber] = useState('');
  const [dayPlans, setDayPlans] = useState([]);
  const [recommendations, setRecommendations] = useState({
    bilan: '',
  });
  const [summary, setSummary] = useState('');
  const [step, setStep] = useState(2);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWeekPlanAndRecipes = async () => {
      try {
        const [weekPlanData, recipesResponse] = await Promise.all([
          api.get(`/coach/weekplans/${weekPlanId}`),
          api.get('/coach/recipes/all?limit=500'),
        ]);

        const recipesData = recipesResponse.recipes || recipesResponse || [];

        // Recuperer l'ID du client depuis le weekplan
        const clientIdFromPlan = weekPlanData.client?._id || weekPlanData.client || weekPlanData.user?._id || weekPlanData.user || clientId;

        // Recuperer les details complets du client pour avoir weight, height, age, gender, sportProfile
        let fullClientDetails = weekPlanData.client || weekPlanData.user;
        if (clientIdFromPlan) {
          try {
            const clientData = await api.get(`/coach/clients/${clientIdFromPlan}`);
            fullClientDetails = clientData;
          } catch (err) {
            console.warn('Could not fetch full client details:', err);
          }
        }

        // Mapping dayType backend -> frontend pour l'affichage
        const DAY_TYPE_REVERSE = {
          'rest': 'OFF',
          'sport': 'ON',
          'active_rest': 'INTENSIF',
        };

        const enrichedDayPlans = (weekPlanData.dayPlans || []).map((dayPlan) => {
          const enrichedMeals = {};
          for (const [mealKey, mealValue] of Object.entries(dayPlan.meals)) {
            if (mealValue) {
              // Trouver la recette par recipeId ou _id
              const recipeId = mealValue.recipeId || mealValue._id;
              const foundRecipe = recipesData.find((recipe) => recipe._id === recipeId);

              // Enrichir avec toutes les donnees necessaires
              enrichedMeals[mealKey] = {
                recipeId: recipeId,
                source: mealValue.source || foundRecipe?.source || 'coach',
                recipeName: mealValue.recipeName || foundRecipe?.name,
                servingMultiplier: mealValue.servingMultiplier || 1,
                _id: recipeId,
                ...(foundRecipe || {}),
              };
            } else {
              enrichedMeals[mealKey] = null;
            }
          }

          return {
            ...dayPlan,
            dayType: DAY_TYPE_REVERSE[dayPlan.dayType] || dayPlan.dayType || 'OFF',
            meals: enrichedMeals,
          };
        });

        setClientDetails(fullClientDetails);
        setWeekNumber(weekPlanData.weekNumber);
        setDayPlans(enrichedDayPlans);
        setRecommendations(weekPlanData.recommendations || { bilan: '' });
        setSummary(weekPlanData.summary);
        setRecipes(recipesData);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching week plan or recipes:', error);
      }
    };

    fetchWeekPlanAndRecipes();
  }, [clientId, weekPlanId]);

  const handleDayPlanChange = (dayIndex, meal, recipeId) => {
    const updatedDayPlans = [...dayPlans];
    if (!updatedDayPlans[dayIndex]) {
      updatedDayPlans[dayIndex] = { day: '', meals: {} };
    }
    updatedDayPlans[dayIndex].meals[meal] = recipeId;
    setDayPlans(updatedDayPlans);
  };

  // Mapping dayType frontend -> backend
  const DAY_TYPE_MAP = {
    'OFF': 'rest',
    'ON': 'sport',
    'INTENSIF': 'active_rest',
  };

  // Transformer un meal pour le backend (mealReferenceSchema)
  const transformMealForBackend = (meal) => {
    if (!meal) return null;
    return {
      recipeId: meal.recipeId || meal._id,
      source: meal.source || 'coach',
      recipeName: meal.recipeName || meal.name,
      servingMultiplier: meal.servingMultiplier || 1,
    };
  };

  const handleUpdateWeekPlan = async () => {
    try {
      const validDayPlans = dayPlans.map((dayPlan) => ({
        day: dayPlan.day,
        dayType: DAY_TYPE_MAP[dayPlan.dayType] || dayPlan.dayType || 'rest',
        meals: {
          breakfast: transformMealForBackend(dayPlan.meals.breakfast),
          morningSnack: transformMealForBackend(dayPlan.meals.morningSnack),
          lunch: transformMealForBackend(dayPlan.meals.lunch),
          snack: transformMealForBackend(dayPlan.meals.snack),
          dinner: transformMealForBackend(dayPlan.meals.dinner),
        },
      }));

      const updatedWeekPlan = {
        weekNumber,
        dayPlans: validDayPlans,
        recommendations,
        summary,
      };

      await api.put(`/coach/weekplans/${weekPlanId}`, updatedWeekPlan);
      setSuccessModalOpen(true);
    } catch (error) {
      console.error('Error updating week plan:', error);
    }
  };

  const nextStep = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const prevStep = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    navigate("/coach/plans/");
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Edition du programme - Coach Yoco</title>
      </Helmet>

      <Box>
        <Typography variant="h1" sx={{ mb: 3 }}>
          Edition du programme
        </Typography>

        {step === 2 && clientDetails && dayPlans.length > 0 && recipes.length > 0 && (
          <RecipeSelection
            weekNumber={weekNumber}
            setWeekNumber={setWeekNumber}
            dayPlans={dayPlans}
            setDayPlans={setDayPlans}
            recipes={recipes}
            handleDayPlanChange={handleDayPlanChange}
            selectedClient={clientDetails}
            userPreferences={clientDetails?.dietaryPreferences ?? []}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        )}

        {step === 3 && (
          <Recommendations
            recommendations={recommendations}
            setRecommendations={setRecommendations}
            handleCreateWeekPlan={handleUpdateWeekPlan}
            selectedClient={clientDetails}
            userPreferences={clientDetails?.dietaryPreferences ?? []}
            prevStep={prevStep}
          />
        )}

        <Dialog open={successModalOpen} onClose={handleCloseSuccessModal}>
          <DialogTitle>Programme mis a jour avec succes</DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseSuccessModal} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CoachEditPlan;
